import { useEffect, useRef, useState } from 'react'
import { STEP_DISPLAY_NAMES, STEP_ORDER } from '#app/utils/screening-progress'

interface ScreeningProgressBarProps {
	currentStep: number
	completedSteps: string[]
	/** Steps completed by skip stay gray instead of filled. */
	skippedSteps?: string[]
	isGuarantor?: boolean
}

export function ScreeningProgressBar({
	currentStep,
	completedSteps,
	skippedSteps = [],
	isGuarantor = false,
}: ScreeningProgressBarProps) {
	const [isVisible, setIsVisible] = useState(true)
	const lastScrollY = useRef(0)

	/* ---------------- Mobile detection ---------------- */
	const [isMobile, setIsMobile] = useState(false)

	useEffect(() => {
		const check = () => setIsMobile(window.innerWidth < 1024)
		check()
		window.addEventListener('resize', check)
		return () => window.removeEventListener('resize', check)
	}, [])

	/* ---------------- Scroll logic ---------------- */
	useEffect(() => {
		lastScrollY.current = window.scrollY
	}, [])

	useEffect(() => {
		let ticking = false

		const handleScroll = () => {
			if (!ticking) {
				window.requestAnimationFrame(() => {
					const currentScrollY = window.scrollY
					const diff = currentScrollY - lastScrollY.current

					if (currentScrollY < 10) {
						setIsVisible(true)
					} else if (diff > 5) {
						setIsVisible(false)
					} else if (diff < -5) {
						setIsVisible(true)
					}

					lastScrollY.current = currentScrollY
					ticking = false
				})
				ticking = true
			}
		}

		window.addEventListener('scroll', handleScroll, { passive: true })
		return () => window.removeEventListener('scroll', handleScroll)
	}, [])

	/* ---------------- Step calculation ---------------- */
	const getMobileSteps = () => {
		const startIndex = Math.min(
			Math.max(currentStep - 1, 0),
			STEP_ORDER.length - 3,
		)
		return STEP_ORDER.slice(startIndex, startIndex + 3)
	}

	const visibleSteps = isMobile ? getMobileSteps() : STEP_ORDER

	useEffect(() => {
		document.body.setAttribute('data-has-screening-progress', 'true')

		return () => {
			document.body.removeAttribute('data-has-screening-progress')
		}
	}, [])

	/* ---------------- Render ---------------- */
	return (
		<div
			className="fixed right-0 left-0 z-40 w-full bg-[#FBFDFD] transition-transform duration-300 ease-in-out"
			style={{
				top: isVisible ? 'var(--navbar-height)' : '0px',
			}}
		>
			<div className="mx-auto px-4 pt-6 sm:px-6 lg:px-[100px]">
				{/* ---------- Step Labels ---------- */}
				<div className="relative mb-3 sm:mb-4">
					<div className="flex items-center justify-between">
						{visibleSteps.map((stepName, index) => {
							const stepNumber = STEP_ORDER.indexOf(stepName) + 1
							const isCurrent = stepNumber === currentStep
							const isPast = stepNumber < currentStep
							const isCompleted = completedSteps.includes(stepName)
							const isSkipped = skippedSteps.includes(stepName)
							const isActive =
								!isSkipped && (isCurrent || isPast || isCompleted)
							const displayName =
								isGuarantor && stepName === 'rental'
									? 'Living'
									: STEP_DISPLAY_NAMES[stepName]

							return (
								<div
									key={stepName}
									className="flex flex-1 flex-col items-center px-1"
								>
									<span
										className={`text-center text-xs transition-colors sm:text-sm md:text-lg ${
											isActive
												? 'font-medium text-[#32302F]'
												: 'font-normal text-[#747474]'
										}`}
									>
										<span>{displayName}</span>
									</span>
								</div>
							)
						})}
					</div>

					{/* ---------- Step Dots ---------- */}
					{visibleSteps.map((stepName, index) => {
						const stepNumber = STEP_ORDER.indexOf(stepName) + 1
						const isCurrent = stepNumber === currentStep
						const isPast = stepNumber < currentStep
						const isCompleted = completedSteps.includes(stepName)
						const isSkipped = skippedSteps.includes(stepName)
						const isActive =
							!isSkipped && (isCurrent || isPast || isCompleted)

						const leftPosition = ((index + 0.5) / visibleSteps.length) * 100

						return (
							<div
								key={`dot-${stepName}`}
								className="absolute z-10 rounded-full transition-colors"
								style={{
									width: '8px',
									height: '8px',
									backgroundColor: isActive ? '#32302F' : '#D5D4DF',
									left: `${leftPosition}%`,
									top: 'calc(100% + 6px)',
									transform: 'translateX(-50%)',
								}}
							/>
						)
					})}
				</div>

				{/* ---------- Progress Bar ---------- */}
				<div className="relative mt-[22px] mb-4 flex items-center gap-3">
					{visibleSteps.map((stepName, index) => {
						const stepNumber = STEP_ORDER.indexOf(stepName) + 1
						const isCurrent = stepNumber === currentStep
						const isPast = stepNumber < currentStep
						const isCompleted = completedSteps.includes(stepName)
						const isSkipped = skippedSteps.includes(stepName)
						const isActive =
							!isSkipped && (isCurrent || isPast || isCompleted)

						return (
							<div
								key={stepName}
								className="relative flex flex-1 flex-col items-center"
							>
								<div className="relative w-full">
									<div
										className={`w-full transition-all duration-300 ${
											isActive
												? 'border-[#32302F] bg-[#32302F]'
												: 'border-gray-300 bg-gray-300'
										}`}
										style={{
											height: '6.75px',
											borderRadius: '3.38px',
											borderWidth: '1px',
											borderStyle: 'solid',
										}}
									/>
								</div>

								{/* {index < visibleSteps.length - 1 && (
									<div
										className="absolute top-0 right-0 z-20 bg-white"
										style={{
											width: '2px',
											height: '6.75px',
										}}
									/>
								)} */}
							</div>
						)
					})}
				</div>

				{/* ---------- Footer Text ---------- */}
				{/* <div className="mt-2">
					<span className="font-medium text-[#32302F] sm:text-sm md:text-[20px]">
						{currentStep}/{TOTAL_STEPS} steps
					</span>
				</div> */}
			</div>
		</div>
	)
}
